import mapValues from "lodash/mapValues";
import { useBreakpointValue } from "native-base";
import React from "react";
import { useWindowDimensions } from "react-native";

type BreakPoints<T = unknown> = {
    base: T,
    xs?: T,
    sm?: T,
    md?: T,
    lg?: T,
}

export default function withResponsiveProps<T, Props>(Component: React.ComponentType<T>, {xs: base, ...breakpoints}: BreakPoints<Props>, addRef: boolean) {
    return React.memo(React.forwardRef((props, ref) => {
        const breakpointProps = useBreakpointValue({
            base,
            ...breakpoints
        });
        return <Component {...props} { ...breakpointProps } ref={addRef ? ref : undefined} />
    }))
}

type ResponsiveFlags = {
    isXS: boolean
    isSM: boolean
    isSmallScreen: boolean
    isMD: boolean
    isLG: boolean
}

/**
 * 
 * @param {Array} [smallScreen]
 * @property {boolean} [smallScreen.0]
 * @property {boolean} [smallScreen.1]
 * @param {Array} [largeScreenProps]
 * @property {boolean} [largeScreenProps.0]
 * @property {boolean} [largeScreenProps.1]
 * @returns {ResponsiveFlags}
 */
const makeObject = ([isXS = false, isSM = false] = [], [isMD = false, isLG = false] = []) => ({ isXS, isSM, isMD, isLG, isSmallScreen: isXS || isSM || isMD })
const showDrawerProps = {
    base: makeObject([true, false, ]),
    xs: makeObject([false, true, ]),
    md: makeObject([], [true]),
    lg: makeObject([], [false, true]),
};


type ResponsiveFlagOptions = Partial<{ includeKeys: (keyof ResponsiveFlags)[], excludeKeys: (keyof ResponsiveFlags)[], addRef: boolean }>;
export function withResponsiveFlags<T>(
    Component: React.ComponentType<T>,
    { includeKeys , excludeKeys, addRef }: ResponsiveFlagOptions = {}
) {
    const normalizedKeys = (includeKeys ?? (Object.keys(makeObject()) as (keyof ResponsiveFlags)[])).filter((key) => !excludeKeys?.includes(key));
    const responsiveProps = mapValues(showDrawerProps, (value, ) => normalizedKeys.reduce((v, k) => ({
        ...v,
        [k]: value[k],
    }), {}));

    return withResponsiveProps(Component, responsiveProps, addRef) as React.ComponentType<T>;
}


export function useResponsiveTextProps (truncate, maxWidth = 0, referenceRestWidth) {
    const {width} = useWindowDimensions();
    const resolvedWidth = truncate ? width : null;
    return React.useMemo(() => {
        if (!truncate) return {};

        if (!maxWidth) return {};
        
        if (resolvedWidth) {
            maxWidth = Math.round(Math.min(maxWidth, resolvedWidth - referenceRestWidth));
        }

        if (isNaN(maxWidth) || maxWidth <= 0) return {};

        return ({ isTruncated: true, maxWidth: `${maxWidth}px` })
    },  [truncate, resolvedWidth, maxWidth, referenceRestWidth]);
}

/**
 * 
 * @param {*} Component 
 * @returns {React.ReactElement}
 */
export function withResponsivePropsResolved(Component) {
    return ({base, xs, sm, md, lg, ...props}) => {
        const breakpointProps = useBreakpointValue({
            base: base ?? xs,
            sm, md, lg,
        });


        return <Component {...props} {...breakpointProps} />
    }
}